import React, { useRef } from 'react';
import { string } from 'prop-types';
import { nanoid } from 'nanoid';

import useQueryString from '../../react-hooks/use-query-string';
import useFetch from '../../react-hooks/use-fetch';
import { stringifyUrl } from '../../utils/url';
import List from '../staff/list';
import Dropdown from '../dropdown';
import Checkbox from '../checkbox';

function StaffListing({
  checkboxId,
  checkboxLabel,
  checkboxQueryString,
  departmentId,
  departmentLabel,
  departmentOptions,
  departmentQueryString,
  emailAriaLabel,
  errorDescription,
  errorHeading,
  fetchUrl,
  heading,
  loadingDataLabel,
  phoneAriaLabel,
  resultsAriaLabel,
  schoolQueryString,
  schoolName,
  sortbyId,
  sortbyLabel,
  sortbyOptions,
  sortbyQueryString,
  externalLinkText
}) {
  const headingId = useRef(`id-${nanoid()}`);
  const [departmentQs, setDepartmentQs] = useQueryString(departmentQueryString);
  const [sortByQs, setSortByQs] = useQueryString(sortbyQueryString);
  const [fulltimeQs, setFulltimeQs] = useQueryString(checkboxQueryString);
  const fetchUrlWithParams = stringifyUrl({
    url: fetchUrl,
    query: {
      [schoolQueryString]: schoolName,
      ...(departmentQs && { [departmentQueryString]: departmentQs }),
      ...(sortByQs && { [sortbyQueryString]: sortByQs }),
      ...(fulltimeQs && { [checkboxQueryString]: fulltimeQs })
    }
  });
  const parsedDepartmentOptions = JSON.parse(departmentOptions);
  const parsedSortbyOptions = JSON.parse(sortbyOptions);
  const { response, error, isLoading } = useFetch(fetchUrlWithParams);

  function handleDepartmentQs(value) {
    setDepartmentQs(value);

    if (value === parsedDepartmentOptions[0].value) {
      setSortByQs(parsedSortbyOptions[0].value);
    } else {
      setSortByQs(parsedSortbyOptions[1].value);
    }
  }

  return (
    <section
      className="staff-listing page-section"
      aria-labelledby={headingId.current}
    >
      <h2 id={headingId.current}>{heading}</h2>
      <div className="staff-listing__filters">
        <div>
          <div className="staff-listing__dropdown">
            <Dropdown
              initialValue={departmentQs}
              id={`${departmentId}-dropdown`}
              onSelect={handleDepartmentQs}
              options={parsedDepartmentOptions}
              visibleLabel={departmentLabel}
            />
          </div>
          {checkboxLabel && (
            <Checkbox
              checked={!!fulltimeQs}
              id={checkboxId}
              label={checkboxLabel}
              onChange={(state) => setFulltimeQs(state)}
            />
          )}
        </div>
        {(!departmentQs ||
          departmentQs === parsedDepartmentOptions[0].value) && (
          <div className="staff-listing__dropdown">
            <Dropdown
              initialValue={sortByQs}
              id={`${sortbyId}-dropdown`}
              onSelect={setSortByQs}
              options={parsedSortbyOptions}
              visibleLabel={sortbyLabel}
            />
          </div>
        )}
      </div>
      <List
        resultsAriaLabel={resultsAriaLabel}
        emailAriaLabel={emailAriaLabel}
        phoneAriaLabel={phoneAriaLabel}
        fetchUrl={fetchUrlWithParams}
        errorDescription={errorDescription}
        errorHeading={errorHeading}
        loadingDataLabel={loadingDataLabel}
        response={response}
        error={error}
        isLoading={isLoading}
        externalLinkText={externalLinkText}
      />
    </section>
  );
}

StaffListing.propTypes = {
  checkboxId: string,
  checkboxLabel: string,
  checkboxQueryString: string,
  departmentId: string.isRequired,
  departmentLabel: string.isRequired,
  departmentOptions: string.isRequired,
  departmentQueryString: string.isRequired,
  emailAriaLabel: string.isRequired,
  errorDescription: string.isRequired,
  errorHeading: string.isRequired,
  externalLinkText: string.isRequired,
  fetchUrl: string.isRequired,
  loadingDataLabel: string.isRequired,
  heading: string.isRequired,
  phoneAriaLabel: string.isRequired,
  resultsAriaLabel: string.isRequired,
  schoolQueryString: string.isRequired,
  schoolName: string.isRequired,
  sortbyId: string.isRequired,
  sortbyLabel: string.isRequired,
  sortbyOptions: string.isRequired,
  sortbyQueryString: string.isRequired
};

export default StaffListing;
